// AI anlatısının bölüm bölüm düzenlenmesi — saf dönüşümler (spec 2026-10-15 §8.4). Sunucu tam içeriği yeniden
// doğrular (NarrativeValidator); buradaki kontroller kullanıcıyı çekmecede tutmak içindir.

export const NARRATIVE_SECTIONS = [
  'overall', 'keyDifferences', 'themes', 'synthesis', 'commentScoreLinks',
  'recommendations', 'roadmap', 'indicators', 'responsibilities', 'nextSteps',
];

export const SECTION_TITLE_KEYS = {
  overall: 'survey.report.overallAssessment',
  keyDifferences: 'survey.report.keyDifferences',
  themes: 'survey.report.section.s05',
  synthesis: 'survey.report.synthesis',
  commentScoreLinks: 'survey.report.commentScore',
  recommendations: 'survey.report.section.s06',
  roadmap: 'survey.report.section.s07',
  indicators: 'survey.report.indicators',
  responsibilities: 'survey.report.responsibilities',
  nextSteps: 'survey.report.nextSteps',
};

export const ASSESSMENTS = ['consistent', 'partial', 'strength'];
export const PRIORITIES = ['high', 'medium', 'low'];

// README "Anlatı (Görev 8)" sınırları.
export const LIMITS = {
  overall: 1200,
  keyDifferences: { max: 5, len: 300 },
  synthesis: { count: 3, len: 300 },
  themes: { name: 80, summary: 600 },
  commentScoreLinks: { codes: 6 },
  recommendations: { max: 6, title: 120, finding: 600, steps: 5, step: 200, outcome: 300, codes: 6 },
  roadmap: { periods: 3, label: 40, subtitle: 60, rows: 8, cell: 160, milestones: 3, milestone: 120 },
  indicators: { max: 8, label: 120 },
  responsibilities: { max: 8, field: 120 },
  nextSteps: { max: 6, title: 80, description: 200 },
};

const clone = (v) => JSON.parse(JSON.stringify(v));
const str = (v) => (v === null || v === undefined ? '' : String(v));
const filled = (v, max) => str(v).trim().length > 0 && str(v).trim().length <= max;
const within = (v, max) => str(v).trim().length <= max;

export function parseCodes(text) {
  const out = [];
  str(text).split(/[\s,;]+/).map((c) => c.trim().toUpperCase()).filter(Boolean).forEach((c) => {
    if (!out.includes(c)) out.push(c);
  });
  return out;
}

const joinCodes = (codes) => (codes || []).join(', ');

const parseTarget = (v) => {
  if (str(v).trim() === '') return null;
  const n = Number(v);
  return Number.isInteger(n) ? n : NaN;
};

export function toForm(section, n) {
  switch (section) {
    case 'overall':
      return { text: n.overallAssessment || '' };
    case 'keyDifferences':
      return { items: [...(n.keyDifferences || [])] };
    case 'synthesis': {
      const items = [...(n.synthesis || [])];
      while (items.length < LIMITS.synthesis.count) items.push('');
      return { items };
    }
    case 'themes':
      return {
        blocks: (n.themes || []).map((b) => ({
          questionId: b.questionId,
          items: b.items.map((i) => ({ id: i.id, name: i.name, summary: i.summary })),
        })),
      };
    case 'commentScoreLinks':
      return {
        items: (n.commentScoreLinks || []).map((l) => ({ themeRef: l.themeRef, codes: joinCodes(l.questionCodes), assessment: l.assessment })),
      };
    case 'recommendations':
      return {
        items: (n.recommendations || []).map((r) => ({
          title: r.title, codes: joinCodes(r.codes), priority: r.priority, finding: r.finding,
          steps: [...r.steps], expectedOutcome: r.expectedOutcome,
        })),
      };
    case 'roadmap':
      return {
        periods: (n.roadmap?.periods || []).map((p) => ({ label: p.label, subtitle: p.subtitle })),
        rows: (n.roadmap?.rows || []).map((r) => ({ area: r.area, cells: [...r.cells] })),
        milestones: [...(n.roadmap?.milestones || [])],
      };
    case 'indicators':
      return {
        items: (n.indicators || []).map((i) => ({ code: i.code, label: i.label, target6: str(i.target6), target12: str(i.target12) })),
      };
    case 'responsibilities':
      return { items: (n.responsibilities || []).map((r) => ({ area: r.area, owner: r.owner, frequency: r.frequency, reportsTo: r.reportsTo })) };
    case 'nextSteps':
      return { items: (n.nextSteps || []).map((s) => ({ title: s.title, description: s.description })) };
    default:
      return {};
  }
}

export function fromForm(section, form, narrative) {
  const n = clone(narrative);
  const trim = (v) => str(v).trim();
  switch (section) {
    case 'overall':
      n.overallAssessment = trim(form.text);
      break;
    case 'keyDifferences':
      n.keyDifferences = form.items.map(trim).filter(Boolean);
      break;
    case 'synthesis':
      n.synthesis = form.items.map(trim);
      break;
    case 'themes':
      // Yalnız ad ve yorum düzenlenir; Id, sayılar ve bloklar sunucudaki gibi kalır (NarrativeMerge sunucuda da korur).
      n.themes = (n.themes || []).map((b) => {
        const fb = form.blocks.find((x) => x.questionId === b.questionId);
        return {
          ...b,
          items: b.items.map((i) => {
            const fi = fb && fb.items.find((x) => x.id === i.id);
            return fi ? { ...i, name: trim(fi.name), summary: trim(fi.summary) } : i;
          }),
        };
      });
      break;
    case 'commentScoreLinks':
      n.commentScoreLinks = form.items.map((l) => ({ themeRef: l.themeRef, questionCodes: parseCodes(l.codes), assessment: l.assessment }));
      break;
    case 'recommendations':
      n.recommendations = form.items.map((r) => ({
        title: trim(r.title), codes: parseCodes(r.codes), priority: r.priority, finding: trim(r.finding),
        steps: r.steps.map(trim).filter(Boolean), expectedOutcome: trim(r.expectedOutcome),
      }));
      break;
    case 'roadmap':
      n.roadmap = {
        periods: form.periods.map((p) => ({ label: trim(p.label), subtitle: trim(p.subtitle) })),
        rows: form.rows.map((r) => ({ area: trim(r.area), cells: r.cells.map(trim) })),
        milestones: form.milestones.map(trim),
      };
      break;
    case 'indicators':
      n.indicators = form.items.map((i) => ({
        code: trim(i.code).toUpperCase(), label: trim(i.label), target6: parseTarget(i.target6), target12: parseTarget(i.target12),
      }));
      break;
    case 'responsibilities':
      n.responsibilities = form.items.map((r) => ({ area: trim(r.area), owner: trim(r.owner), frequency: trim(r.frequency), reportsTo: trim(r.reportsTo) }));
      break;
    case 'nextSteps':
      n.nextSteps = form.items.map((s) => ({ title: trim(s.title), description: trim(s.description) }));
      break;
    default:
      break;
  }
  return n;
}

export function emptyItem(section) {
  switch (section) {
    case 'keyDifferences': return '';
    case 'commentScoreLinks': return { themeRef: '', codes: '', assessment: 'consistent' };
    case 'recommendations': return { title: '', codes: '', priority: 'medium', finding: '', steps: [''], expectedOutcome: '' };
    case 'roadmap': return { area: '', cells: ['', '', ''] };
    case 'indicators': return { code: '', label: '', target6: '', target12: '' };
    case 'responsibilities': return { area: '', owner: '', frequency: '', reportsTo: '' };
    case 'nextSteps': return { title: '', description: '' };
    default: return null;
  }
}

export function maxItems(section) {
  switch (section) {
    case 'keyDifferences': return LIMITS.keyDifferences.max;
    case 'recommendations': return LIMITS.recommendations.max;
    case 'roadmap': return LIMITS.roadmap.rows;
    case 'indicators': return LIMITS.indicators.max;
    case 'responsibilities': return LIMITS.responsibilities.max;
    case 'nextSteps': return LIMITS.nextSteps.max;
    case 'commentScoreLinks': return 20;
    default: return null;
  }
}

const validTarget = (v) => {
  const n = parseTarget(v);
  return n === null || (Number.isInteger(n) && n >= 0 && n <= 100);
};

export function validateForm(section, form) {
  const L = LIMITS;
  switch (section) {
    case 'overall':
      return filled(form.text, L.overall);
    case 'keyDifferences':
      return form.items.length <= L.keyDifferences.max && form.items.every((i) => filled(i, L.keyDifferences.len));
    case 'synthesis':
      return form.items.length === L.synthesis.count && form.items.every((i) => filled(i, L.synthesis.len));
    case 'themes':
      return form.blocks.every((b) => b.items.every((i) => filled(i.name, L.themes.name) && within(i.summary, L.themes.summary)));
    case 'commentScoreLinks':
      return form.items.every((l) => str(l.themeRef) !== '' && ASSESSMENTS.includes(l.assessment)
        && parseCodes(l.codes).length <= L.commentScoreLinks.codes);
    case 'recommendations':
      return form.items.length <= L.recommendations.max && form.items.every((r) => filled(r.title, L.recommendations.title)
        && PRIORITIES.includes(r.priority) && within(r.finding, L.recommendations.finding)
        && within(r.expectedOutcome, L.recommendations.outcome) && parseCodes(r.codes).length <= L.recommendations.codes
        && r.steps.filter((s) => str(s).trim()).length >= 1 && r.steps.length <= L.recommendations.steps
        && r.steps.every((s) => within(s, L.recommendations.step)));
    case 'roadmap':
      return form.periods.length === L.roadmap.periods
        && form.periods.every((p) => filled(p.label, L.roadmap.label) && within(p.subtitle, L.roadmap.subtitle))
        && form.rows.length <= L.roadmap.rows
        && form.rows.every((r) => filled(r.area, L.roadmap.cell) && r.cells.length === 3 && r.cells.every((c) => within(c, L.roadmap.cell)))
        && form.milestones.length === L.roadmap.milestones && form.milestones.every((m) => filled(m, L.roadmap.milestone));
    case 'indicators':
      return form.items.length <= L.indicators.max && form.items.every((i) => str(i.code).trim() !== ''
        && filled(i.label, L.indicators.label) && validTarget(i.target6) && validTarget(i.target12));
    case 'responsibilities':
      return form.items.length <= L.responsibilities.max && form.items.every((r) => filled(r.area, L.responsibilities.field)
        && within(r.owner, L.responsibilities.field) && within(r.frequency, L.responsibilities.field)
        && within(r.reportsTo, L.responsibilities.field));
    case 'nextSteps':
      return form.items.length <= L.nextSteps.max && form.items.every((s) => filled(s.title, L.nextSteps.title)
        && within(s.description, L.nextSteps.description));
    default:
      return false;
  }
}

export function themeRefOptions(narrative, data) {
  if (!narrative) return [];
  return (narrative.themes || []).flatMap((b) => {
    const ft = (data?.freeText || []).find((f) => f.questionId === b.questionId);
    const prefix = ft && ft.code ? `${ft.code} · ` : '';
    return b.items.map((i) => ({ id: `${b.questionId}/${i.id}`, name: `${prefix}${i.name}` }));
  });
}
